import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Paper } from "@mui/material";
// services
import Constants from "../../../services/constants";
// types
import type { SxProps, Theme } from "@mui/material";
import type { ParseKeys } from "i18next";
import type { CustomForceGraphLink, CustomGraphLinkData } from "../../../types/misc";

const TOOLTIP_SX = {
	position: "absolute",
	maxWidth: 320,
	p: "8px 10px",
	bgcolor: "rgba(30, 30, 30, 0.92)",
	color: "#fff",
	fontSize: 12,
	lineHeight: 1.4,
	pointerEvents: "none",
	zIndex: 10,
	whiteSpace: "normal",
} as const satisfies SxProps<Theme>;

const UNREACHABLE_COST = 7;

const EMBER_ROUTER = 2;
const EMBER_END_DEVICE = 3;
const EMBER_SLEEPY_END_DEVICE = 4;

type NetworkMapKey = Extract<ParseKeys, `gateway.networkMap.${string}`>;

type TooltipEntry =
	Readonly<{ kind: "labeled"; labelKey: NetworkMapKey; valueKey: NetworkMapKey }> |
	Readonly<{ kind: "single"; key: NetworkMapKey; options?: Readonly<Record<string, string | number | null | undefined>> }>;

const formatSecondsFriendly = (seconds: number): string => {
	if (seconds < 120) return `${seconds}s`;
	if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
	return `${Math.round(seconds / 3600)}h`;
};

// index 0 = 10s; index 1..14 = 2^n minutes
const getEndDeviceTimeoutSeconds = (index: number): number | undefined => {
	if (index < 0 || index > 14) return undefined;
	return index === 0 ? 10 : 2 ** index * 60;
};

const getChildOriginKey = (childNodeType: number | null): NetworkMapKey => {
	switch (childNodeType) {
		case EMBER_ROUTER: return "gateway.networkMap.diagnostics.origin.childTableRouter";
		case EMBER_END_DEVICE: return "gateway.networkMap.diagnostics.origin.childTableEndDevice";
		case EMBER_SLEEPY_END_DEVICE: return "gateway.networkMap.diagnostics.origin.childTableSleepyEndDevice";
		default: return "gateway.networkMap.diagnostics.origin.childTable";
	}
};

const getOriginKey = (data: CustomGraphLinkData): NetworkMapKey => {
	if (data.source === "child") return getChildOriginKey(data.childNodeType);
	if (data.source === "neighbor") return "gateway.networkMap.diagnostics.origin.neighborTable";
	return "gateway.networkMap.diagnostics.origin.directRouter";
};

const getRelationshipKey = (relationship: string): NetworkMapKey => {
	switch (relationship) {
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Parent: return "gateway.networkMap.parent";
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Child: return "gateway.networkMap.child";
		case Constants.ZigbeeDeviceNetworkInfo.RelationShips.Sibling: return "gateway.networkMap.sibling";
		default: return "gateway.networkMap.special";
	}
};

const getEndDeviceTimeoutEntry = (index: number | null): TooltipEntry | null => {
	if (index === null) return null;
	const seconds = getEndDeviceTimeoutSeconds(index);
	return seconds === undefined
		? { kind: "single", key: "gateway.networkMap.diagnostics.tooltip.configuredTimeoutIndex", options: { index } }
		: { kind: "single", key: "gateway.networkMap.diagnostics.tooltip.configuredTimeoutFormatted", options: { duration: formatSecondsFriendly(seconds), index } };
};

const getLinkDetailEntries = (data: CustomGraphLinkData): Array<TooltipEntry> => {
	const entries: Array<TooltipEntry> = [
		{ kind: "labeled", labelKey: "gateway.networkMap.diagnostics.tooltip.originLabel", valueKey: getOriginKey(data) },
	];
	if (data.relationship) entries.push({ kind: "labeled", labelKey: "gateway.networkMap.diagnostics.tooltip.relationshipLabel", valueKey: getRelationshipKey(data.relationship) });
	if (data.lqi !== null) entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.lqiLine", options: { pct: data.lqi, abs: data.lqiAbs } });
	if (data.age !== null) {
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.ageLine", options: { age: data.age, seconds: data.age * 16 } });
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.ageExplanation", options: { age: data.age } });
	}
	if (data.timeoutRemainingSeconds !== null) entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.remainingLine", options: { seconds: data.timeoutRemainingSeconds } });
	if (data.childTimeoutIndex !== null) {
		const timeoutEntry = getEndDeviceTimeoutEntry(data.childTimeoutIndex);
		if (timeoutEntry) {
			entries.push(timeoutEntry);
			entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.configuredTimeoutExplanation" });
		}
	}
	if (data.txPowerDbm !== null) entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.txPowerLine", options: { dbm: data.txPowerDbm } });
	if (data.phy !== null) entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.phyLine", options: { phy: data.phy } });
	if (data.incomingCost !== null) {
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.inCostLine", options: { cost: data.incomingCost } });
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.inCostExplanation" });
	}
	if (data.outgoingCost !== null) {
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.outCostLine", options: { cost: data.outgoingCost } });
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.outCostExplanation" });
	}
	if (data.incomingCost === UNREACHABLE_COST || data.outgoingCost === UNREACHABLE_COST) {
		entries.push({ kind: "single", key: "gateway.networkMap.diagnostics.tooltip.unreachableHint" });
	}
	return entries;
};

type Props = Readonly<{
	link: CustomForceGraphLink;
	pos: { x: number; y: number };
}>;

const NetworkMapLinkTooltip = ({ link, pos }: Props) => {
	const { t: tRaw } = useTranslation();
	const t = tRaw as (key: string, options?: Readonly<Record<string, unknown>>) => string;

	return (
		<Paper
			elevation={4}
			style={{ left: pos.x + 14, top: pos.y + 14 }}
			sx={TOOLTIP_SX}
		>
			{getLinkDetailEntries(link.data).map((entry, index) => (
				<div key={index}>
					{(entry.kind === "labeled")
						? `${t(entry.labelKey)}: ${t(entry.valueKey)}`
						: t(entry.key, entry.options)
					}
				</div>
			))}
		</Paper>
	);
};

export default memo(NetworkMapLinkTooltip);
